import database from '../database';
import lodash from 'lodash';
const { debounce, isEqual } = lodash;
import ViewCriteria, { NON_USER_FILTER_TYPES } from './viewCriteria';
import liftGate from './liftGate';
import _emptyDbView, { EMPTY_ARRAY } from './emptyDbView';
import logging from '@sstdev/lib_logging';
import getGlobalConfig from '../globalConfig';
import { getRelationMetadata } from '../metadata';
import { SYNC_TYPES } from '../constants';
import fetchAndUpsertTemporaryQueryResult from './fetchAndUpsertTemporaryQueryResult';
import getLocalOnlySearchReason from './getLocalOnlySearchReason';
import resolveGateDisagreement from './resolveGateDisagreement';
import { getKey, setKey } from '../pageLoadStorage';
import network from '../network';
import ObjectId from '../ObjectId';
import { GATE_FILTER_ID } from '../filters/forTempQueryResult';

const CHECK_EQUALITY_RECORD_COUNT_BREAKPOINT = 70;
const { minFullTextSearchTermLength } = getGlobalConfig();

// Clears the #332 stage-1 marker and notifies rebuildListeners so React-side
// consumers (useDbView) re-render. Without the explicit notify, a flag-only
// transition with no data change leaves the "Displaying initial results,
// searching..." indicator on screen even though the underlying state has
// already moved on.
function clearAwaitingFirstServerPage(dbView) {
    if (!dbView.awaitingFirstServerPage) return;
    dbView.awaitingFirstServerPage = false;
    dbView.rebuildListeners.forEach(listener => listener(dbView));
}

// Reset the #332 stage-2 paging + display gate for a view so the next rebuild re-fetches
// page 0 fresh and re-includes any records added since the last fetch. Shared by the
// user-driven applyFilters path (onBeforeChange, below) and the post-import refresh
// (refreshViewsForRelation, below): the latter lets records that were upserted into Loki by
// socket notifications during an import — but are hidden by the gate — appear without the user
// having to change a filter ("click around"). removeFilters(GATE_FILTER_ID) triggers a loki
// reapplyFilters -> rebuild, which recomputes the (now un-gated) data, re-renders consumers,
// and runs the stage-2 re-fetch.
function resetPagingAndGate(dbView, viewCriteria, limitedSyncSizeRelation) {
    dbView.queryId = new ObjectId().toString();
    // Rewinds paging, drops the previous query's accumulated gate _ids, and removes the gate
    // filter — the last of which triggers a loki reapplyFilters -> rebuild. The previous
    // queryId's gate would otherwise constrain stage 1 to OLD-queryId-tagged rows, hiding local
    // matches of the new filter set until stage 2 re-applies.
    liftGate(dbView, viewCriteria);
    // The stage-2 display handoff (#332) only applies to limitSyncSize relations — those
    // are the only ones that get a server-paged fetch via getNextPageFromServer. Setting
    // the flag for other relations would strand the banner since no clear path runs.
    // This is the one thing resetPagingAndGate adds over a bare gate lift: it re-arms stage 2.
    if (limitedSyncSizeRelation) dbView.awaitingFirstServerPage = true;
}

function getDbViewReferences() {
    let dbViewReferences = getKey('dbViewReferences');
    if (dbViewReferences == null) {
        dbViewReferences = {};
        setKey('dbViewReferences', dbViewReferences);
    }
    return dbViewReferences;
}

const PAGE_SIZE = getGlobalConfig().pageSizeForLimitSyncSizeRelations;
const _p = {
    getOnRebuild,
    getNextPageFromServer,
    getRelationMetadata,
    fetchAndUpsertTemporaryQueryResult,
    getLocalOnlySearchReason,
    resolveGateDisagreement,
    getDbViewReferences,
    network,
    PAGE_SIZE
};
export const _private = _p;
export const emptyDbView = _emptyDbView;
export { LOCAL_ONLY_SEARCH_REASONS } from './getLocalOnlySearchReason';

/**
 * Fascaded pattern over top of the lokiJs dynamicView to simplify
 * things (a little bit :-) ).
 * @param {string} namespaceTitle
 * @param {string} relationTitle
 * @param {string} dbViewName - determines app-wide unique identity of view
 * @returns dbView
 */
const _getDbView = async function getDbView(
    namespaceTitle,
    relationTitle,
    dbViewName = `${namespaceTitle}_${relationTitle}_default`,
    clearFilters = false
) {
    if (!getDbViewReferences()[dbViewName]) {
        getDbViewReferences()[dbViewName] = createView(namespaceTitle, relationTitle, dbViewName, clearFilters);
    }
    return await getDbViewReferences()[dbViewName];
};
_getDbView._private = _p;
export const getDbView = _getDbView;

function viewContainsId(lokiView, _id) {
    return lokiView.data().some(record => record._id === _id);
}

async function createView(namespaceTitle, relationTitle, dbViewName, clearFilters) {
    const relation = _p.getRelationMetadata(namespaceTitle, relationTitle, false);
    const dbViewToExport = {
        namespace: namespaceTitle,
        relation: relationTitle,
        viewName: dbViewName,
        data: EMPTY_ARRAY,
        count: 0,
        page: 0,
        serverSideOffset: 0,
        // Regenerated on every user-driven applyFilters (see onBeforeChange below). Tags records
        // bulk-upserted by fetchAndUpsertTemporaryQueryResult (for truncation protection, #332).
        queryId: new ObjectId().toString(),
        // The _ids this view's stage-2 server fetch(es) returned, accumulated across pages and
        // used as the display gate (#332). Per-view, so sibling views over the same relation
        // can't clobber it. Reset on every user-driven applyFilters (onBeforeChange below).
        gateResultIds: [],
        // Armed on creation so a freshly opened limitSyncSize view gets exactly one stage-2
        // fetch. `resetPagingAndGate` re-arms it on every user-driven applyFilters, but a view
        // with no filter hNode children never calls applyFilters (useDbView skips it to avoid
        // clobbering SearchPane filters), so without this it would never reach stage 2 at all
        // once the rebuild trigger is latched on this flag (#1712).
        awaitingFirstServerPage: relation?.limitSyncSize ?? false,
        viewCriteria: null,
        changeListeners: [],
        // rebuilds can occur even without changes to the data in the view (e.g. when filters change, but the filter does not change the data)
        rebuildListeners: [],
        cleanupOperations: [],
        getNextPage: async () => {
            // some relations are not explicitly defined (e.g. application notifications)
            if (relation == null) {
                throw new Error('dbView.getNextPage can only be called for relations explicitly defined.');
            }
            return await _p.getNextPageFromServer(dbViewToExport);
        }
    };

    await database.isDbReady();
    const db = database.get();
    // Get underlying lokijs database to access collection directly
    const loki = await db.abstractDbRoot.waitTillDbReady;
    const collectionName = `${namespaceTitle}_${relationTitle}`;
    // Noop if already initialized, create collection otherwise
    db.initializeDb(namespaceTitle, relationTitle);
    const collection = loki.getCollection(collectionName);
    if (collection == null) {
        throw new Error(
            `No collection was found for namespace_relation ${collectionName}.  This likely means the metadata is not setup correctly for this namespace/collection.  It may need "Offline Sync" checked in Blockly.`
        );
    }

    let _lokiView = collection.getDynamicView(dbViewName);
    if (_lokiView == null) {
        _lokiView = collection.addDynamicView(dbViewName, { minRebuildInterval: 5 });
        _lokiView.namespace = namespaceTitle;
        _lokiView.relation = relationTitle;
    } else {
        if (clearFilters) {
            _lokiView.removeFilters();
        }
    }

    const _onRebuild = getOnRebuild(_lokiView, dbViewToExport, dbViewName);
    _lokiView.addListener('rebuild', _onRebuild);
    // When debugging you can track down rebuilds with this:
    // _view.addListener('rebuild', () => {
    //     debugger;
    // });
    _onRebuild();

    const viewCriteria = ViewCriteria(_lokiView);
    const limitedSyncSizeRelation = relation?.limitSyncSize ?? false;
    // Expose whether this view applies the #332 stage-2 display gate, so external callers
    // (e.g. the post-import refresh) can find and reset only the gated views.
    dbViewToExport.limitSyncSize = limitedSyncSizeRelation;
    // Reset view page count when the view sort or filters change.
    viewCriteria.onBeforeChange((type, _filters, filterId) => {
        if (type !== 'applyFilters') return;
        // The Phase 3 gate (#332) is internal infrastructure applied via the reserved
        // GATE_FILTER_ID; it must not be treated as a user-driven query change
        // (no queryId regen, no page reset).
        if (filterId === GATE_FILTER_ID) return;
        logging.debug(`[DBVIEW] - Resetting page count and server side offset for ${dbViewName} due to ${type}.`);
        resetPagingAndGate(dbViewToExport, viewCriteria, limitedSyncSizeRelation);
    });
    dbViewToExport.viewCriteria = viewCriteria;
    // If the viewCriteria creates any cleanup operations, be sure to run those as part of
    // the cleanup for this dbView.
    dbViewToExport.cleanupOperations.push(viewCriteria.cleanupAll);
    // The lokiView's persistent DynamicView survives release (see createView above), so
    // the gate filter would leak into the next session without explicit removal.
    dbViewToExport.cleanupOperations.push(() => viewCriteria.removeFilters(GATE_FILTER_ID));
    dbViewToExport.lokiView = _lokiView;
    dbViewToExport.name = _lokiView.name;
    dbViewToExport.containsId = _id => viewContainsId(_lokiView, _id);

    // Allow cleanup
    dbViewToExport.release = getOnRelease(dbViewToExport, _onRebuild);

    return dbViewToExport;
}

function getOnRelease(dbView, _onRebuild) {
    return function release() {
        try {
            // If memory consumption of views becomes a problem then this function may also need
            // to destroy the actual loki dynamicView.
            // Otherwise, don't remove the loki dynamicview filters here because it will cause a dynamicview
            // rebuild and the screen will flicker in summary detail view.
            // This is because a cancel (or just clicking between records (which causes a cancel))
            // of the summary detail render will release this view, but then the summary only render
            // will happen immediately after, and the same loki dynamicview will be needed for that.
            const _lokiView = dbView.lokiView;
            logging.debug(`[DBVIEW] - releasing ${dbView.viewName}.`);
            _lokiView.removeListener('rebuild', _onRebuild);
            _onRebuild.cancel();
            dbView.lokiView = undefined;
            dbView.data = EMPTY_ARRAY;
            dbView.count = 0;
            dbView.cleanupOperations.forEach(c => c());
            // Clear this reference so that this cleanup happens only once, even if there are
            // multiple consumers of the view.
            dbView.release = () => {};
            // Publish cleared out data to all consumers
            dbView.rebuildListeners.forEach(listener => listener(dbView));
            dbView.changeListeners.forEach(listener => listener(dbView));
            delete getDbViewReferences()[dbView.viewName];
        } catch (err) {
            logging.debug(`[DBVIEW] - Error while releasing ${dbView.viewName}.`);
            logging.error(err);
        }
    };
}

function getOnRebuild(_lokiView, dbViewToExport, dbViewName) {
    const { namespace: namespaceTitle, relation: relationTitle } = dbViewToExport;
    const relationMetadata = _p.getRelationMetadata(namespaceTitle, relationTitle, false);
    const limitedSyncSizeRelation = relationMetadata?.limitSyncSize ?? false;
    const maxSyncSize = relationMetadata?.maxSyncSize;
    const debounced = debounce(() => {
        // This is throttled because lokijs dynamicView.data() "resolves any pending
        // filtering and sorting, then returns document array as result."
        const newData = _lokiView.data();
        const filters = dbViewToExport.viewCriteria.getFiltersFromView();
        // Set before the deep-equality early return below: the reason can change while the rows do
        // not (a server search that fails back to the local one answers with the same rows it was
        // already showing), and that early return still notifies rebuildListeners, so the grid
        // re-renders with the new reason.
        dbViewToExport.localOnlySearchReason = _p.getLocalOnlySearchReason(filters, relationMetadata);
        if (filters?.fullTextSearch?.serverResults != null) {
            // Set serverSideOffset to the count of all full text search results
            dbViewToExport.serverSideOffset = Object.values(filters.fullTextSearch.serverResults).reduce(
                (count, result) => (count += result.length),
                0
            );
        }
        // Ideally, we would only check for deep equality if the effort is something less than rerendering.
        // This is pretty difficult to measure though.  Here's the "skateboard" approach:
        // First open of a limitSyncSize grid with empty cache must reach the stage-2
        // getNextPage() call below — whose finally clears awaitingFirstServerPage —
        // even though newData ([]) deep-equals the initial EMPTY_ARRAY.
        const awaitingFirstPage = limitedSyncSizeRelation && dbViewToExport.awaitingFirstServerPage;
        if (!awaitingFirstPage && newData.length < CHECK_EQUALITY_RECORD_COUNT_BREAKPOINT) {
            if (isEqual(newData, dbViewToExport.data)) {
                // See "wrapperToTrackIfRebuilding" below.
                dbViewToExport.rebuildInProgress = false;
                dbViewToExport.rebuildListeners.forEach(listener => listener(dbViewToExport));
                return;
            }
        }

        dbViewToExport.data = newData;
        // Avoid unnecessary renders by using a constant reference for empty data
        if (dbViewToExport.data.length === 0) {
            dbViewToExport.data = EMPTY_ARRAY;
        }
        dbViewToExport.count = dbViewToExport.data.length;

        // Create a new method reference here (as opposed to just keeping the existing one which
        // does the exact same thing) because useEffect calls that need to be aware of a change
        // in the view data may (in the useEffect dependency array) only be looking at the
        // containsId method reference on the view -- meaning the useEffect
        // won't run even though the result of the containsId method might be different
        // with the new data.
        dbViewToExport.containsId = _id => viewContainsId(_lokiView, _id);

        // Log view rebuilds with count, filters and sort
        logging.debug(
            // FYI - This is different from the Loki rebuilds because we might skip this rebuild if the lokijs
            // dataset is small enough to measure deep equality and find it equal to the old dataset.
            `[DBVIEW] Rebuild ${dbViewName}, Count: ${dbViewToExport.count}` //, \n    ${logViewCriteria(_lokiView)}`
        );
        // See "wrapperToTrackIfRebuilding" below.
        dbViewToExport.rebuildInProgress = false;
        // inform dependencies (like useDbView) that the data has been rebuilt
        dbViewToExport.changeListeners.forEach(listener => listener(dbViewToExport));
        dbViewToExport.rebuildListeners.forEach(listener => listener(dbViewToExport));

        if (relationTitle !== 'shiftSummary') {
            const nextPageThreshold = (limitedSyncSizeRelation && maxSyncSize) ? maxSyncSize : _p.PAGE_SIZE;
            // Under limitSyncSize the local collection is only the newest N rows, so once it is
            // filled to the sync cap it is a biased, incomplete sample: a user filter matching a
            // full local page does NOT mean the server has no further/different matches. (e.g. a
            // report Company filter that all recent local rows happen to satisfy would otherwise
            // stay above the threshold and silently filter only the local working set instead of
            // the full BigQuery-backed dataset.) Force the stage-2 fetch, but scope it tightly to
            // the case where it is actually needed — ALL of:
            //   1. a fresh user-driven applyFilters is pending (`awaitingFirstServerPage`, set in
            //      onBeforeChange) — not a passive re-render or streamed data change;
            //   2. a real user find filter is active (not just orderBy/paging) — default and
            //      unfiltered grids never force a fetch;
            //   3. local is filled to the sync cap (`data.length >= nextPageThreshold`), i.e. the
            //      collection was plausibly truncated and is incomplete. A small/complete
            //      limitSyncSize relation (local below the cap) is already covered by the
            //      below-threshold arm of `shouldFetchNextPage`, so this forced path introduces
            //      no *additional* round-trip for it.
            // getNextPageFromServer clears `awaitingFirstServerPage`, so this does not loop.
            const hasActiveUserFilter = Object.keys(filters).some(f => !NON_USER_FILTER_TYPES.has(f));
            const localFilledToCap = dbViewToExport.data.length >= nextPageThreshold;
            const forceServerFetchForFilter =
                dbViewToExport.awaitingFirstServerPage && hasActiveUserFilter && localFilledToCap;
            // `awaitingFirstServerPage` gates BOTH reasons to fetch, not just the forced one.
            // "local is below the threshold" is not a terminating condition: for a relation whose
            // ENTIRE row count is smaller than a page (e.g. `identity:role` with 3 roles), it is
            // permanently true — there is no further record anywhere to raise the count. The fetch
            // re-upserts the rows it just pulled, which dirties the loki collection, which rebuilds
            // the view, which fetches again, forever (#1712). Latching on the same flag the forced
            // path uses makes stage 2 run at most once per user-driven query (view creation or a
            // fresh applyFilters both arm it), and getNextPageFromServer's finally clears it.
            // Further pages still come from an explicit user request (doingGetMore -> getNextPage).
            const shouldFetchNextPage =
                dbViewToExport.awaitingFirstServerPage && (!localFilledToCap || forceServerFetchForFilter);
            if (shouldFetchNextPage && limitedSyncSizeRelation) {
                // If fullTextSearch has serverResults, the server already returned all matching IDs.
                // No need to fetch more pages - the search is complete.
                const serverResults = filters?.fullTextSearch?.serverResults;
                if (serverResults != null && Object.keys(serverResults).length > 0) {
                    logging.debug(
                        `[DBVIEW] Rebuild count was ${dbViewToExport.data.length}, but fullTextSearch already has serverResults. Skipping getNextPage.`
                    );
                    // FT preFilter already satisfied the stage-2 promise (server search returned ids).
                    // Clear the #332 stage-1 banner so it doesn't strand here.
                    clearAwaitingFirstServerPage(dbViewToExport);
                } else {
                    logging.info(
                        forceServerFetchForFilter
                            ? `[DBVIEW] Local page full (${dbViewToExport.data.length}) but a user filter changed under limitSyncSize; forcing next page from server.`
                            : `[DBVIEW] Rebuild count was only ${dbViewToExport.data.length}; getting next page.`
                    );
                    // getNextPageFromServer's finally clears awaitingFirstServerPage.
                    dbViewToExport.getNextPage();
                }
            } else if (limitedSyncSizeRelation) {
                // limitSyncSize relation that will not fetch — either the stage-2 latch is
                // already spent for this query, or local is filled to the cap with no user
                // filter. Clear the #332 banner here so it doesn't strand.
                clearAwaitingFirstServerPage(dbViewToExport);
            }
        }
    }, 250);

    // The debounced function (obviously) could be called any number of times, so we need this
    // wrapper to show we have a need to do (or are currently doing) our rebuild logic.
    // The actual debounced function will set it to false when done.
    const wrapperToTrackIfRebuilding = () => {
        dbViewToExport.rebuildInProgress = true;
        debounced();
    };

    // Since we are wrapping the debounce, expose the cancel function on the wrapper.
    wrapperToTrackIfRebuilding.cancel = debounced.cancel;
    return wrapperToTrackIfRebuilding;
}

async function getNextPageFromServer(dbView) {
    // This code is only relevant for relations with limited sync size.
    const { namespace: namespaceTitle, relation: relationTitle, lokiView, viewCriteria } = dbView;

    let result = [];
    // The #332 stage-1 banner clears at the end regardless of which branch (or which
    // early return) ran. Wrapping the relation-null and MAX_COLLECTION_SIZE guards in
    // the try/finally too keeps the banner from stranding under those paths.
    try {
        // Stage-2 fetches must not run while the startup sync is still working through the
        // primary relations. A view mounting during boot would otherwise claim its relation
        // (syncOneRelation sets syncStartTime), so when the obtrusive startup sync reached
        // that relation it saw a sync "in progress", skipped it, and released the progress
        // dialog while the real multi-minute pull ran on unobtrusively in the background.
        await database.get().synchronization.initialSyncFinished;
        const relation = _p.getRelationMetadata(namespaceTitle, relationTitle, false);

        // If this is not a synchronized relation (e.g. application notification), skip
        if (relation == null) {
            logging.warn('dbView.getNextPage can only be called for relations explicitly defined.');
            return;
        }

        const MAX_COLLECTION_SIZE = getGlobalConfig().maxRecordsInLimitedSizeCollection;

        // If there is a 'paged' filter use that to get the page size. Otherwise, use the
        // global constant.
        const filters = viewCriteria.getFiltersFromView();
        const pageSize = filters?.paged?.limit ?? _p.PAGE_SIZE;
        // If the maximum local collection size has been reached, then we cannot request
        // another batch and the request cannot be filtered and fulfilled locally
        if (lokiView.collection.data.length + pageSize > MAX_COLLECTION_SIZE) {
            // Collection is at capacity; skip this fetch. Background truncation after sync
            // bounds local size — no user-facing error (#332).
            logging.debug(
                '[dbViews] Local collection at capacity; skipping getNextPage fetch (truncation bounds size).'
            );
            return;
        }
        // Only filters that genuinely narrow the result set may send a view down the gated
        // stage-2 path — see NON_USER_FILTER_TYPES.
        const findFilters = Object.keys(filters).filter(f => !NON_USER_FILTER_TYPES.has(f));

        // If there are no filters that actually find data, then just do a regular sync to
        // get the next page.
        if (
            findFilters.length === 0 ||
            (filters?.fullTextSearch != null &&
                (filters.fullTextSearch?.searchTerm?.length ?? 0) < minFullTextSearchTermLength)
        ) {
            // A gate can outlive the user filter that created it. applyFilters only publishes
            // the before-change that runs resetPagingAndGate when the requested filters are not
            // already applied (see _applyFilters), so a remount, a SearchPane clear, or a
            // DynamicView carried over from a previous session can leave a stale
            // `_id: {$in: <old page>}` in the pipeline. Nothing else lifts it once there are no
            // find filters left, and every record created since would keep failing that $in.
            // Test against the echoed filter rather than dbView.gateResultIds: on a restored
            // view the pipeline still holds the gate while the fresh dbView has no ids.
            if (filters.forTempQueryResult != null) {
                liftGate(dbView, viewCriteria);
            }
            const db = database.get();
            ({ result } = await db.synchronization.syncOneRelation(
                { namespaceTitle, relationTitle },
                SYNC_TYPES.sizeLimitedFullBatch
            ));
        } else {
            filters.paged = filters.paged ?? { page: dbView.page };
            const newPage = (filters.paged.page ?? 0) + 1;
            filters.paged.page = newPage;
            filters.paged.currentOffset = dbView.serverSideOffset;
            filters.paged.limit = pageSize;
            // dbView.page is committed below, after we know a server request will be made.
            // Pre-fix it was committed up here, so the non-FT offline guard returned []
            // with dbView.page already advanced — next online attempt could skip a page.
            // The FT branch passes dbView.viewName as the applyFilters filterId so the
            // pipeline entry lives under a stable per-view uid (separate from the gate's
            // GATE_FILTER_ID used by the non-FT branch).
            const ftFilterId = `${dbView.viewName}`;

            // Log page requests
            logging.debug(
                `[DBVIEW] Getting next page (page: ${newPage}, offset: ${dbView.serverSideOffset}) for ${ftFilterId}.`
            );

            if (filters.fullTextSearch != null) {
                if (filters.fullTextSearch.searchTerm.length >= minFullTextSearchTermLength) {
                    // Rewound below if this page never lands. `dbView.page` is the durable paging
                    // state: `getFiltersFromView` does not carry `paged`, so `filters.paged` is
                    // rebuilt from `dbView.page` on the next call and needs no rewind of its own.
                    const pageBeforeRequest = dbView.page;
                    dbView.page = newPage;
                    // Full text search pulls pages of records from the server as part of the filter.
                    // suppressBeforeChange: this apply IS the paging step, not a new user query. The
                    // FT branch shares its filterId with the user's search box (KeySearch applies
                    // under the same `viewName` uid), so onBeforeChange cannot tell the two apart and
                    // would run resetPagingAndGate here — stomping the `dbView.page = newPage` set
                    // immediately above back to 0 and regenerating queryId. That pinned FT paging to
                    // page 1 forever and invalidated each stage-2 gate as stale (see :466), leaving
                    // the grid re-fetching and rebuilding in a visible flicker.
                    await viewCriteria.applyFilters(filters, ftFilterId, undefined, {
                        suppressBeforeChange: true
                    });
                    // Ask whether THIS page landed, not merely whether some page did. A server
                    // search that fails after an earlier page succeeded leaves the earlier page in
                    // place, so `serverResults != null` is not evidence that this one arrived.
                    const pageResults = filters.fullTextSearch.serverResults?.[`page${newPage}`];
                    if (pageResults != null) {
                        result = pageResults;
                    } else {
                        // No page arrived: preFilter discarded the results because the server search
                        // failed, or none was attempted (offline, or the relation cannot be searched
                        // server-side). Either way nothing was consumed, so give the page number back
                        // — leaving it advanced makes a recovered server resume a page ahead and skip
                        // a page of results for good. This is the same rule the stage-2 branch below
                        // follows by committing `dbView.page` only once a fetch is certain.
                        dbView.page = pageBeforeRequest;
                        logging.debug(
                            `[DBVIEW] fullTextSearch for ${ftFilterId} page ${newPage} returned no server page; ` +
                                `rewound to page ${pageBeforeRequest} and falling back to the local filter.`
                        );
                        result = [];
                    }
                } else {
                    return [];
                }
            } else {
                // Phase 3 (#332) stage-2 handoff path: offline guard → fetch → bulkUpsert (tags
                // records for truncation protection) → apply display gate that constrains the
                // view to this fetch's own result _ids.
                const { isOnline } = await _p.network.getStatus();
                if (!isOnline) {
                    // Offline → stay in stage 1 (local-only data); no fetch, no gate, no
                    // page advance. Next online attempt resumes at the same page.
                    return [];
                }
                // getFiltersFromView echoes the gate's bbFilter (forTempQueryResult) after the
                // first stage-2 apply. getQueryUrl already guards via getUriComponent, but
                // strip locally so downstream URL builders never see it (defense in depth).
                delete filters.forTempQueryResult;
                const gateQueryId = dbView.queryId;
                // Commit dbView.page now that we know a fetch will be made.
                // fetchAndUpsertTemporaryQueryResult reads dbView.page when building the URL.
                dbView.page = newPage;
                ({ result } = await _p.fetchAndUpsertTemporaryQueryResult(dbView, gateQueryId));
                // If the user changed filters mid-fetch, dbView.queryId has advanced. Skip the
                // stale gate apply — the next fetch's gate will overwrite by uid anyway, and
                // touching serverSideOffset with the stale page length would desync subsequent
                // pages of the new query.
                if (dbView.queryId !== gateQueryId) {
                    logging.debug(
                        `[DBVIEW] Discarding stale stage-2 gate for ${dbView.viewName}: gateQueryId=${gateQueryId}, current=${dbView.queryId}`
                    );
                    return result;
                }
                dbView.serverSideOffset += result?.length ?? 0;
                // Gate the view to the _ids THIS view's server fetch returned, accumulated across
                // pages. The gate is keyed to the view's own result set (not a shared record tag),
                // so concurrent dbViews over the same relation can't clobber each other's gate — a
                // single shared `meta.forTempQueryResult` scalar previously let the last-fetching
                // view orphan every other view's gate, blanking it (#898, Inventory > Count empty).
                // Mirrors fullTextSearch's `{_id: {$in: serverResults}}` gate. (The record tag is
                // still written by fetchAndUpsertTemporaryQueryResult for truncation protection.)
                // Skip the gate when stage 2 returned 0 records: an empty `$in` would hide every
                // local row (including background-synced records) even though the server gave us
                // nothing fresh. Falling through keeps stage-1 visible until the next refresh.
                if ((result?.length ?? 0) > 0) {
                    // Dedupe across pages and drop any falsy _ids so the $in gate stays minimal and valid.
                    const pageIds = result.map(r => r._id).filter(Boolean);
                    // A page shorter than the limit is the server's COMPLETE answer to these
                    // criteria, so any row the view matched locally and the page omits is a
                    // disagreement rather than a row waiting on a later page — settle it instead
                    // of deleting the row (#1978). Only page 1 can disagree: liftGate rewound
                    // paging and removed the gate on the user-driven filter change, so the view is
                    // still ungated here, while from page 2 on it is already gated (and an omitted
                    // row may legitimately arrive on a later page).
                    const { confirmedIds, incomplete } =
                        newPage === 1 && result.length < pageSize
                            ? await _p.resolveGateDisagreement({
                                  dbView,
                                  queryId: gateQueryId,
                                  pageIds,
                                  fetchAndUpsert: _p.fetchAndUpsertTemporaryQueryResult
                              })
                            : { confirmedIds: [], incomplete: false };
                    // Second stale-query check. The one above covers the page fetch; resolving the
                    // disagreement is another round trip, and the user can change filters during it.
                    // Without this the OLD page's ids get gated onto the NEW query — resolveGateDisagreement
                    // discarding its own answer does not help, because `pageIds` come from the caller.
                    if (dbView.queryId !== gateQueryId) {
                        logging.debug(
                            `[DBVIEW] Discarding stale stage-2 gate for ${dbView.viewName} after disagreement resolution: gateQueryId=${gateQueryId}, current=${dbView.queryId}`
                        );
                        return result;
                    }
                    // The disagreement could not be settled, so there is no basis for deleting the
                    // rows the page omitted. Leave the view ungated until the next refresh rather
                    // than gating on an answer already known to be missing rows (#1978) — the same
                    // reasoning as the zero-result case above.
                    if (incomplete) {
                        logging.info(
                            `[DBVIEW] Leaving ${dbView.viewName} ungated: the stage-2 disagreement could not be settled.`
                        );
                        return result;
                    }
                    dbView.gateResultIds = [...new Set([...(dbView.gateResultIds ?? []), ...pageIds, ...confirmedIds])];
                    await viewCriteria.applyFilters({ forTempQueryResult: dbView.gateResultIds }, GATE_FILTER_ID);
                }
            }
        }
        return result;
    } finally {
        clearAwaitingFirstServerPage(dbView);
    }
}

export async function destroyDbView(viewName) {
    const viewPromise = getDbViewReferences()[viewName];
    if (!viewPromise) return;
    logging.debug(`[DBVIEW] destroying ${viewName}`);
    delete getDbViewReferences()[viewName];
    const view = await viewPromise;
    const collection = view.lokiView.collection;
    view.release();
    collection.removeDynamicView(viewName);
}

export async function destroyDbViewForRelation(namespaceTitle, relationTitle) {
    const views = Object.values(_p.getDbViewReferences());
    for (let i = 0; i < views.length; i++) {
        const viewPromise = views[i];
        const view = await viewPromise;
        if (view.namespace === namespaceTitle && view.relation === relationTitle) {
            const collection = view?.lokiView?.collection;
            view.release();
            collection?.removeDynamicView(view.viewName);
        }
    }
}

/**
 * Reset the #332 display gate for every active view over the given relation so records that
 * were upserted into Loki after the last stage-2 fetch (e.g. rows just created by an import and
 * delivered via socket notifications) become visible without the user changing a filter.
 *
 * Only limitSyncSize views apply the gate; non-gated views already show fresh records and are
 * skipped. Mirrors what a user-driven filter change does (see onBeforeChange above).
 * @param {string} namespaceTitle
 * @param {string} relationTitle
 */
export async function refreshViewsForRelation(namespaceTitle, relationTitle) {
    const views = Object.values(_p.getDbViewReferences());
    for (let i = 0; i < views.length; i++) {
        const view = await views[i];
        if (view?.lokiView == null || view.viewCriteria == null) continue;
        if (view.namespace !== namespaceTitle || view.relation !== relationTitle) continue;
        // Only limitSyncSize views hide fresh records behind the #332 gate.
        if (!view.limitSyncSize) continue;
        logging.debug(`[DBVIEW] - Refreshing #332 gate for ${view.viewName} after import.`);
        resetPagingAndGate(view, view.viewCriteria, view.limitSyncSize);
    }
}

export function logViewCriteria(_lokiView) {
    return `CRITERIA: ${JSON.stringify(
        _lokiView?.filterPipeline?.filter(f => f.type === 'find'),
        null,
        3
    ).replace(/\n/g, '\n    ')}, \n    SORT: [\n        ${_lokiView?.sortCriteria
        ?.map(sort => `${sort[0]}: ${sort[1] ? 'desc' : 'asc'} uid: ${sort[2].join(', ')}`)
        .join('\n        ')}\n    ]`;
}
